import React, { useState, useEffect } from 'react';
import {
    Box,
    Card,
    Typography,
    Grid,
    TextField,
    MenuItem,
    CircularProgress,
    LinearProgress,
} from '@material-ui/core';
import { Bar, Radar } from 'react-chartjs-2';

export default function CohortReports({ classes }) {
    const [loading, setLoading] = useState(false);
    const [selectedCohort, setSelectedCohort] = useState('');
    const [cohorts, setCohorts] = useState([]);
    const [cohortData, setCohortData] = useState(null);
    const [cohortInfo, setCohortInfo] = useState(null);

    useEffect(() => {
        fetchCohorts();
    }, []);

    useEffect(() => {
        if (selectedCohort) {
            fetchCohortAnalytics(selectedCohort);
        } else {
            setCohortData(null);
            setCohortInfo(null);
        }
    }, [selectedCohort]);

    const fetchCohorts = async () => {
        try {
            const response = await fetch('/api/cohort', {
                credentials: 'include',
            });

            if (response.ok) {
                const data = await response.json();
                setCohorts(data);
            }
        } catch (error) {
            console.error('Error fetching cohorts:', error);
        }
    };

    const fetchCohortAnalytics = async (cohortId) => {
        setLoading(true);

        try {
            const response = await fetch(`/api/reports/cohort/${cohortId}`, {
                credentials: 'include',
            });

            if (response.ok) {
                const data = await response.json();
                setCohortData(data);
                setCohortInfo(data.cohortInfo);
            } else {
                setCohortData(null);
                setCohortInfo(null);
            }
        } catch (error) {
            console.error('Error fetching cohort analytics:', error);
            setCohortData(null);
            setCohortInfo(null);
        } finally {
            setLoading(false);
        }
    };

    /*
     * Existing comparative chart data.
     */
    const cohortComparativeData = cohortData
        ? {
            labels: (cohortData.comparativeAnalysis || []).map(
                item => item.dimension
            ),
            datasets: [
                {
                    label: 'Cohort Average',
                    data: (cohortData.comparativeAnalysis || []).map(
                        item => item.cohort_avg
                    ),
                    backgroundColor: 'rgba(217, 98, 48, 0.7)',
                },
                {
                    label: 'Overall Average',
                    data: (cohortData.comparativeAnalysis || []).map(
                        item => item.overall_avg
                    ),
                    backgroundColor: 'rgba(99, 132, 255, 0.7)',
                },
            ],
        }
        : {
            labels: [],
            datasets: [],
        };

    /*
     * Existing dimension radar chart data.
     */
    const cohortDimensionRadarData = cohortData
        ? {
            labels: [
                'Innovation',
                'Risk Taking',
                'Proactivity',
                'Planning',
                'Execution',
                'Intrinsic Motivation',
            ],
            datasets: [
                {
                    label: 'Cohort Performance',
                    data: [
                        cohortData.dimensionPerformance?.innovation || 0,
                        cohortData.dimensionPerformance?.risk_taking || 0,
                        cohortData.dimensionPerformance?.proactivity || 0,
                        cohortData.dimensionPerformance?.planning || 0,
                        cohortData.dimensionPerformance?.execution || 0,
                        cohortData.dimensionPerformance?.intrinsic_motivation || 0,
                    ],
                    backgroundColor: 'rgba(217, 98, 48, 0.2)',
                    borderColor: 'rgb(217, 98, 48)',
                    borderWidth: 2,
                    pointBackgroundColor: 'rgb(217, 98, 48)',
                },
            ],
        }
        : {
            labels: [],
            datasets: [],
        };

    /*
     * Completion colour aligned with the backend completion interpretation:
     * 80%+ = healthy
     * 50-79.9% = moderate
     * below 50% = low
     */
    const getCompletionColor = (rate) => {
        if (rate >= 80) return '#4caf50';
        if (rate >= 50) return '#ff9800';
        return '#f44336';
    };

    const getCompletionBackground = (status) => {
        if (status === 'good') {
            return '#f1f8f4';
        }

        if (status === 'warning') {
            return '#fff8e1';
        }

        if (status === 'critical') {
            return '#ffebee';
        }

        return '#f5f5f5';
    };

    const formatDifference = (difference) => {
        const value = Number(difference);

        if (Number.isNaN(value)) {
            return '0.00';
        }

        return value > 0
            ? `+${value.toFixed(2)}`
            : value.toFixed(2);
    };

    const coordinatorInsights =
        cohortData?.coordinatorInsights || null;

    const strengths =
        coordinatorInsights?.strengths || [];

    const developmentAreas =
        coordinatorInsights?.developmentAreas || [];

    const alignedAreas =
        coordinatorInsights?.alignedAreas || [];

    const completionInsight =
        coordinatorInsights?.completion || null;

    const strongestDimension =
        coordinatorInsights?.summary?.strongestDimension || null;

    const priorityDevelopmentArea =
        coordinatorInsights?.summary?.priorityDevelopmentArea || null;

    const comparisonContext =
        coordinatorInsights?.summary?.comparisonContext ||
        'Cohort averages are compared with the overall comparison population.';

    return (
        <div>
            <Typography variant="h5" gutterBottom>
                Cohort Analysis
            </Typography>

            <Typography
                variant="body2"
                color="textSecondary"
                style={{ marginBottom: 24 }}
            >
                Review participation, cohort performance, comparative strengths,
                development priorities and teaching recommendations.
            </Typography>

            {/* Cohort Selection */}
            <Box mb={3}>
                <TextField
                    select
                    label="Select Cohort"
                    value={selectedCohort}
                    onChange={(e) => setSelectedCohort(e.target.value)}
                    variant="outlined"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                >
                    <MenuItem value="">
                        Select a cohort...
                    </MenuItem>

                    {cohorts.map((cohort) => (
                        <MenuItem
                            key={cohort.id}
                            value={cohort.id}
                        >
                            {cohort.name}
                        </MenuItem>
                    ))}
                </TextField>
            </Box>

            {loading ? (
                <Box
                    display="flex"
                    justifyContent="center"
                    p={4}
                >
                    <CircularProgress />
                </Box>
            ) : (
                <>
                    {selectedCohort && cohortData && (
                        <>
                            {/* Cohort Header */}
                            {cohortInfo && (
                                <Card
                                    variant="outlined"
                                    style={{
                                        padding: 16,
                                        marginBottom: 24,
                                        backgroundColor: '#f5f5f5',
                                    }}
                                >
                                    <Typography
                                        variant="h5"
                                        gutterBottom
                                    >
                                        {cohortInfo.name}
                                    </Typography>

                                    {cohortInfo.description && (
                                        <Typography
                                            color="textSecondary"
                                            paragraph
                                        >
                                            {cohortInfo.description}
                                        </Typography>
                                    )}
                                </Card>
                            )}

                            {/* Participation Metrics */}
                            <Grid
                                container
                                spacing={3}
                                style={{ marginBottom: 24 }}
                            >
                                <Grid
                                    item
                                    xs={12}
                                    sm={3}
                                >
                                    <Card
                                        variant="outlined"
                                        style={{
                                            padding: 16,
                                            textAlign: 'center',
                                            height: '100%',
                                        }}
                                    >
                                        <Typography
                                            color="textSecondary"
                                            gutterBottom
                                        >
                                            Completion Rate
                                        </Typography>

                                        <Typography
                                            variant="h4"
                                            style={{
                                                color: getCompletionColor(
                                                    cohortData.participation.completionRate
                                                ),
                                            }}
                                        >
                                            {cohortData.participation.completionRate}%
                                        </Typography>

                                        <LinearProgress
                                            variant="determinate"
                                            value={
                                                cohortData.participation.completionRate
                                            }
                                            style={{ marginTop: 8 }}
                                        />
                                    </Card>
                                </Grid>

                                <Grid
                                    item
                                    xs={12}
                                    sm={3}
                                >
                                    <Card
                                        variant="outlined"
                                        style={{
                                            padding: 16,
                                            textAlign: 'center',
                                            height: '100%',
                                        }}
                                    >
                                        <Typography
                                            color="textSecondary"
                                            gutterBottom
                                        >
                                            Completed
                                        </Typography>

                                        <Typography variant="h4">
                                            {
                                                cohortData.participation
                                                    .completedSurveys
                                            }
                                        </Typography>

                                        <Typography variant="body2">
                                            Surveys
                                        </Typography>
                                    </Card>
                                </Grid>

                                <Grid
                                    item
                                    xs={12}
                                    sm={3}
                                >
                                    <Card
                                        variant="outlined"
                                        style={{
                                            padding: 16,
                                            textAlign: 'center',
                                            height: '100%',
                                        }}
                                    >
                                        <Typography
                                            color="textSecondary"
                                            gutterBottom
                                        >
                                            Pending
                                        </Typography>

                                        <Typography variant="h4">
                                            {
                                                cohortData.participation
                                                    .pendingSurveys
                                            }
                                        </Typography>

                                        <Typography variant="body2">
                                            Surveys
                                        </Typography>
                                    </Card>
                                </Grid>

                                <Grid
                                    item
                                    xs={12}
                                    sm={3}
                                >
                                    <Card
                                        variant="outlined"
                                        style={{
                                            padding: 16,
                                            textAlign: 'center',
                                            height: '100%',
                                        }}
                                    >
                                        <Typography
                                            color="textSecondary"
                                            gutterBottom
                                        >
                                            Total Members
                                        </Typography>

                                        <Typography variant="h4">
                                            {
                                                cohortData.participation
                                                    .totalMembers
                                            }
                                        </Typography>

                                        <Typography
                                            variant="body2"
                                            color="textSecondary"
                                        >
                                            Cohort participants
                                        </Typography>
                                    </Card>
                                </Grid>
                            </Grid>

                            {/* D-09 Coordinator Insights */}
                            {coordinatorInsights && (
                                <Card
                                    variant="outlined"
                                    style={{
                                        padding: 20,
                                        marginBottom: 24,
                                    }}
                                >
                                    <Typography
                                        variant="h5"
                                        gutterBottom
                                    >
                                        Coordinator Insights
                                    </Typography>

                                    <Typography
                                        variant="body2"
                                        color="textSecondary"
                                        paragraph
                                    >
                                        This section translates the cohort
                                        analytics into participation guidance,
                                        comparative strengths and development
                                        priorities for coordinators.
                                    </Typography>

                                    {/* Completion / Participation Insight */}
                                    {completionInsight && (
                                        <Box
                                            mb={3}
                                            p={2}
                                            style={{
                                                backgroundColor:
                                                    getCompletionBackground(
                                                        completionInsight.status
                                                    ),
                                                borderRadius: 4,
                                            }}
                                        >
                                            <Typography
                                                variant="subtitle1"
                                                style={{ fontWeight: 600 }}
                                                gutterBottom
                                            >
                                                Participation Status
                                            </Typography>

                                            <Typography
                                                variant="body1"
                                                gutterBottom
                                            >
                                                {
                                                    completionInsight.interpretation
                                                }
                                            </Typography>

                                            <Typography
                                                variant="body2"
                                                color="textSecondary"
                                            >
                                                <strong>
                                                    Coordinator action:
                                                </strong>{' '}
                                                {completionInsight.action}
                                            </Typography>
                                        </Box>
                                    )}

                                    {/* High-level Summary */}
                                    <Grid
                                        container
                                        spacing={3}
                                        style={{ marginBottom: 8 }}
                                    >
                                        <Grid
                                            item
                                            xs={12}
                                            md={6}
                                        >
                                            <Card
                                                variant="outlined"
                                                style={{
                                                    padding: 16,
                                                    height: '100%',
                                                    borderLeft:
                                                        '4px solid #4caf50',
                                                }}
                                            >
                                                <Typography
                                                    variant="subtitle1"
                                                    style={{
                                                        fontWeight: 600,
                                                    }}
                                                    gutterBottom
                                                >
                                                    Strongest Relative Area
                                                </Typography>

                                                {strongestDimension ? (
                                                    <>
                                                        <Typography
                                                            variant="h6"
                                                            gutterBottom
                                                        >
                                                            {
                                                                strongestDimension.dimension
                                                            }
                                                        </Typography>

                                                        <Typography
                                                            variant="body2"
                                                            color="textSecondary"
                                                        >
                                                            Cohort average:{' '}
                                                            {
                                                                strongestDimension.cohortAverage
                                                            }
                                                            {' | '}
                                                            Overall average:{' '}
                                                            {
                                                                strongestDimension.overallAverage
                                                            }
                                                        </Typography>

                                                        <Typography
                                                            variant="body2"
                                                            style={{
                                                                marginTop: 8,
                                                            }}
                                                        >
                                                            This cohort is{' '}
                                                            <strong>
                                                                {formatDifference(
                                                                    strongestDimension.difference
                                                                )}
                                                            </strong>{' '}
                                                            points above the
                                                            overall comparison
                                                            average in this
                                                            dimension.
                                                        </Typography>
                                                    </>
                                                ) : (
                                                    <Typography
                                                        variant="body2"
                                                        color="textSecondary"
                                                    >
                                                        No dimension is
                                                        currently above the
                                                        overall comparison
                                                        average.
                                                    </Typography>
                                                )}
                                            </Card>
                                        </Grid>

                                        <Grid
                                            item
                                            xs={12}
                                            md={6}
                                        >
                                            <Card
                                                variant="outlined"
                                                style={{
                                                    padding: 16,
                                                    height: '100%',
                                                    borderLeft:
                                                        '4px solid #ff9800',
                                                }}
                                            >
                                                <Typography
                                                    variant="subtitle1"
                                                    style={{
                                                        fontWeight: 600,
                                                    }}
                                                    gutterBottom
                                                >
                                                    Priority Development Area
                                                </Typography>

                                                {priorityDevelopmentArea ? (
                                                    <>
                                                        <Typography
                                                            variant="h6"
                                                            gutterBottom
                                                        >
                                                            {
                                                                priorityDevelopmentArea.dimension
                                                            }
                                                        </Typography>

                                                        <Typography
                                                            variant="body2"
                                                            color="textSecondary"
                                                        >
                                                            Cohort average:{' '}
                                                            {
                                                                priorityDevelopmentArea.cohortAverage
                                                            }
                                                            {' | '}
                                                            Overall average:{' '}
                                                            {
                                                                priorityDevelopmentArea.overallAverage
                                                            }
                                                        </Typography>

                                                        <Typography
                                                            variant="body2"
                                                            style={{
                                                                marginTop: 8,
                                                            }}
                                                        >
                                                            This is the largest
                                                            relative gap in the
                                                            current comparison,
                                                            at{' '}
                                                            <strong>
                                                                {formatDifference(
                                                                    priorityDevelopmentArea.difference
                                                                )}
                                                            </strong>{' '}
                                                            points.
                                                        </Typography>
                                                    </>
                                                ) : (
                                                    <Typography
                                                        variant="body2"
                                                        color="textSecondary"
                                                    >
                                                        No dimension is
                                                        currently below the
                                                        overall comparison
                                                        average.
                                                    </Typography>
                                                )}
                                            </Card>
                                        </Grid>
                                    </Grid>

                                    {/* Detailed Strengths and Development Areas */}
                                    <Grid
                                        container
                                        spacing={3}
                                        style={{ marginTop: 4 }}
                                    >
                                        <Grid
                                            item
                                            xs={12}
                                            md={6}
                                        >
                                            <Typography
                                                variant="h6"
                                                gutterBottom
                                            >
                                                Cohort Strengths
                                            </Typography>

                                            {strengths.length > 0 ? (
                                                strengths.map(
                                                    (item, index) => (
                                                        <Box
                                                            key={`${item.dimension}-${index}`}
                                                            mb={1}
                                                            p={2}
                                                            style={{
                                                                backgroundColor:
                                                                    '#f1f8f4',
                                                                borderRadius: 4,
                                                            }}
                                                        >
                                                            <Typography
                                                                variant="subtitle2"
                                                                style={{
                                                                    fontWeight: 600,
                                                                }}
                                                            >
                                                                {item.dimension}
                                                            </Typography>

                                                            <Typography
                                                                variant="body2"
                                                            >
                                                                Cohort{' '}
                                                                {
                                                                    item.cohortAverage
                                                                }
                                                                {' vs '}
                                                                overall{' '}
                                                                {
                                                                    item.overallAverage
                                                                }
                                                                {' ('}
                                                                {formatDifference(
                                                                    item.difference
                                                                )}
                                                                {')'}
                                                            </Typography>
                                                        </Box>
                                                    )
                                                )
                                            ) : (
                                                <Typography
                                                    variant="body2"
                                                    color="textSecondary"
                                                >
                                                    No comparative strengths
                                                    are currently identified.
                                                </Typography>
                                            )}
                                        </Grid>

                                        <Grid
                                            item
                                            xs={12}
                                            md={6}
                                        >
                                            <Typography
                                                variant="h6"
                                                gutterBottom
                                            >
                                                Development Areas
                                            </Typography>

                                            {developmentAreas.length > 0 ? (
                                                developmentAreas.map(
                                                    (item, index) => (
                                                        <Box
                                                            key={`${item.dimension}-${index}`}
                                                            mb={1}
                                                            p={2}
                                                            style={{
                                                                backgroundColor:
                                                                    '#fff8e1',
                                                                borderRadius: 4,
                                                            }}
                                                        >
                                                            <Typography
                                                                variant="subtitle2"
                                                                style={{
                                                                    fontWeight: 600,
                                                                }}
                                                            >
                                                                {item.dimension}
                                                            </Typography>

                                                            <Typography
                                                                variant="body2"
                                                            >
                                                                Cohort{' '}
                                                                {
                                                                    item.cohortAverage
                                                                }
                                                                {' vs '}
                                                                overall{' '}
                                                                {
                                                                    item.overallAverage
                                                                }
                                                                {' ('}
                                                                {formatDifference(
                                                                    item.difference
                                                                )}
                                                                {')'}
                                                            </Typography>
                                                        </Box>
                                                    )
                                                )
                                            ) : (
                                                <Typography
                                                    variant="body2"
                                                    color="textSecondary"
                                                >
                                                    No comparative development
                                                    areas are currently
                                                    identified.
                                                </Typography>
                                            )}
                                        </Grid>
                                    </Grid>

                                    {/* Equal-to-comparison dimensions */}
                                    {alignedAreas.length > 0 && (
                                        <Box mt={3}>
                                            <Typography
                                                variant="subtitle1"
                                                style={{ fontWeight: 600 }}
                                                gutterBottom
                                            >
                                                Areas Aligned with the Overall
                                                Average
                                            </Typography>

                                            <Typography
                                                variant="body2"
                                                color="textSecondary"
                                            >
                                                {alignedAreas
                                                    .map(
                                                        item =>
                                                            item.dimension
                                                    )
                                                    .join(', ')}
                                            </Typography>
                                        </Box>
                                    )}

                                    {/* Comparison explanation */}
                                    <Box
                                        mt={3}
                                        p={2}
                                        style={{
                                            backgroundColor: '#f5f5f5',
                                            borderRadius: 4,
                                        }}
                                    >
                                        <Typography
                                            variant="subtitle2"
                                            style={{ fontWeight: 600 }}
                                            gutterBottom
                                        >
                                            Comparison Context
                                        </Typography>

                                        <Typography
                                            variant="body2"
                                            color="textSecondary"
                                        >
                                            {comparisonContext}
                                            {' '}
                                            Strengths and development areas
                                            indicate relative differences from
                                            this comparison and should not be
                                            interpreted as absolute judgements
                                            of participant capability.
                                        </Typography>
                                    </Box>
                                </Card>
                            )}

                            {/* Existing charts */}
                            <Grid container spacing={3}>
                                {/* Comparative Analysis */}
                                {(cohortData.comparativeAnalysis || [])
                                    .length > 0 && (
                                    <Grid
                                        item
                                        xs={12}
                                        md={6}
                                    >
                                        <Card
                                            variant="outlined"
                                            style={{ padding: 16 }}
                                        >
                                            <Typography
                                                variant="h6"
                                                gutterBottom
                                            >
                                                Cohort vs Overall Performance
                                            </Typography>

                                            <Typography
                                                variant="body2"
                                                color="textSecondary"
                                                style={{
                                                    marginBottom: 12,
                                                }}
                                            >
                                                Compare the selected cohort
                                                average with the overall
                                                comparison average across key
                                                dimensions.
                                            </Typography>

                                            <div style={{ height: 400 }}>
                                                <Bar
                                                    data={
                                                        cohortComparativeData
                                                    }
                                                    options={{
                                                        responsive: true,
                                                        maintainAspectRatio:
                                                            false,
                                                        scales: {
                                                            y: {
                                                                beginAtZero:
                                                                    true,
                                                                max: 20,
                                                            },
                                                        },
                                                    }}
                                                />
                                            </div>
                                        </Card>
                                    </Grid>
                                )}

                                {/* Dimension Radar Chart */}
                                <Grid
                                    item
                                    xs={12}
                                    md={6}
                                >
                                    <Card
                                        variant="outlined"
                                        style={{ padding: 16 }}
                                    >
                                        <Typography
                                            variant="h6"
                                            gutterBottom
                                        >
                                            Dimension Performance Profile
                                        </Typography>

                                        <Typography
                                            variant="body2"
                                            color="textSecondary"
                                            style={{
                                                marginBottom: 12,
                                            }}
                                        >
                                            View the cohort's average profile
                                            across the key entrepreneurial
                                            dimensions.
                                        </Typography>

                                        <div style={{ height: 400 }}>
                                            <Radar
                                                data={
                                                    cohortDimensionRadarData
                                                }
                                                options={{
                                                    responsive: true,
                                                    maintainAspectRatio:
                                                        false,
                                                    scales: {
                                                        r: {
                                                            beginAtZero: true,
                                                            max: 20,
                                                            ticks: {
                                                                stepSize: 5,
                                                            },
                                                        },
                                                    },
                                                }}
                                            />
                                        </div>
                                    </Card>
                                </Grid>

                                {/* Existing Educational Recommendations */}
                                <Grid
                                    item
                                    xs={12}
                                >
                                    <Card
                                        variant="outlined"
                                        style={{ padding: 16 }}
                                    >
                                        <Typography
                                            variant="h6"
                                            gutterBottom
                                        >
                                            Educational Recommendations
                                        </Typography>

                                        <Typography
                                            variant="body2"
                                            color="textSecondary"
                                            style={{
                                                marginBottom: 16,
                                            }}
                                        >
                                            Suggested teaching and
                                            facilitation actions based on the
                                            existing cohort analysis.
                                        </Typography>

                                        <Box
                                            style={{
                                                maxHeight: 400,
                                                overflow: 'auto',
                                            }}
                                        >
                                            {(cohortData.recommendations || [])
                                                .length > 0 ? (
                                                cohortData.recommendations.map(
                                                    (
                                                        recommendation,
                                                        index
                                                    ) => (
                                                        <Box
                                                            key={index}
                                                            mb={2}
                                                            p={2}
                                                            style={{
                                                                backgroundColor:
                                                                    '#f9f9f9',
                                                                borderRadius: 4,
                                                            }}
                                                        >
                                                            <Typography variant="body2">
                                                                {
                                                                    recommendation
                                                                }
                                                            </Typography>
                                                        </Box>
                                                    )
                                                )
                                            ) : (
                                                <Typography
                                                    variant="body2"
                                                    color="textSecondary"
                                                >
                                                    No educational
                                                    recommendations are
                                                    currently available.
                                                </Typography>
                                            )}
                                        </Box>
                                    </Card>
                                </Grid>
                            </Grid>
                        </>
                    )}

                    {selectedCohort &&
                        !cohortData &&
                        !loading && (
                            <Typography
                                align="center"
                                color="textSecondary"
                            >
                                No data available for this cohort yet.
                            </Typography>
                        )}

                    {!selectedCohort && (
                        <Typography
                            align="center"
                            color="textSecondary"
                        >
                            Please select a cohort to view detailed analysis.
                        </Typography>
                    )}
                </>
            )}
        </div>
    );
}